import { Profile } from '@app/Profile/Profile';
import * as React from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Dashboard } from '@app/Dashboard/Dashboard';
import { Servers } from '@app/Datacenter/Servers/Servers';
import ServerDetail from '@app/Datacenter/Servers/ServerDetail';
import { VirtualMachines } from '@app/Datacenter/VirtualMachines/VirtualMachines';
import VirtualMachineDetail from '@app/Datacenter/VirtualMachines/VirtualMachineDetail';
import { Containers } from '@app/Datacenter/Containers/Containers';
import { Emails } from '@app/Datacenter/Emails/Emails';
import EmailsDetail from '@app/Datacenter/Emails/EmailsDetail';
import { Certificates } from '@app/Datacenter/Certificates/Certificates';
import CertificateDetail from '@app/Datacenter/Certificates/CertificateDetail';
import { Domains as DatacenterDomains } from '@app/Datacenter/Domains/Domains';
import DomainNameDetail from '@app/Datacenter/Domains/DomainNameDetail';
import DomainRegistration from '@app/Datacenter/Domains/Register/DomainRegistration';
import DomainTransfer from '@app/Datacenter/Domains/Transfer/DomainTransfer';
import DomainTrade from '@app/Datacenter/Domains/Trade/DomainTrade';
import DomainOrderReturn from '@app/Datacenter/Domains/Register/DomainOrderReturn';
import { PubkeyCreate, PubkeyDetail, PubkeyEdit, PubkeyList } from '@app/Datacenter/Pubkeys';
import Networks from '@app/Datacenter/Networks/Networks';
import NetworkDetail from '@app/Datacenter/Networks/NetworkDetail';
import Vpn from '@app/Datacenter/Vpn/Vpn';
import { Backups } from '@app/Datacenter/Backups/Backups';
import BackupDetail from '@app/Datacenter/Backups/BackupDetail';
import { Licenses } from '@app/Datacenter/Licenses/Licenses';
import GpuServers from '@app/Datacenter/GpuServers/GpuServers';
import GpuServerDetail from '@app/Datacenter/GpuServers/GpuServerDetail';
import StartGpuServer from '@app/Datacenter/GpuServers/Start/StartGpuServer';
import { NotFound } from '@app/NotFound/NotFound';
import Organization from './Organization/Organization';
import DomainDeleteResources from './Organization/Domains/DomainDeleteResources';
import { AppLayout } from './AppLayout/AppLayout';
import { Datacenter } from './Datacenter/Datacenter';
import { Support } from './Support/Support';
import { Roadmap } from './Roadmap/Roadmap';
import { Newsletter } from './Admin/Newsletter/Newsletter';
import { Quotes } from './Admin/Quotes/Quotes';
import { ProductsAndPrices } from './Admin/ProductsAndPrices/ProductsAndPrices';
import { DomainPricing } from './Admin/DomainPricing/DomainPricing';
import { CertificatePlatforms } from './Admin/CertificatePlatforms/CertificatePlatforms';
import { BackupAttributions } from './Admin/BackupAttributions/BackupAttributions';
import { Spla } from './Admin/Spla/Spla';
import { Wallets } from './Admin/Wallets/Wallets';
import { WalletDetail } from './Admin/Wallets/WalletDetail';
import { Verda } from './Admin/Verda/Verda';
import VerdaServerDetail from './Admin/Verda/VerdaServerDetail';
import { Reconciliation } from './Admin/Reconciliation/Reconciliation';
import { SettlementDetail as ReconciliationSettlementDetail } from './Admin/Reconciliation/SettlementDetail';

import Privacy from './Privacy';
import Tos from './Tos';
import Invoices from './Organization/Invoices';
import Prices from './Organization/Prices';
import Team from './Organization/Team';
import Domains from './Organization/Domains/Domains';
import DomainDetail from './Organization/Domains/DomainDetail';
import { Customers } from './Organization/Customers/Customers';
import { Agreements } from './Organization/Agreements/Agreements';
import { Agent } from './Organization/Agent/Agent';
import PaymentReturn from './Organization/PaymentReturn';

export interface IAppRoute {
  label?: string; // Excluding the label will exclude the route from the nav sidebar in AppLayout
  path: string;
  title: string;
  routes?: undefined;
  adminOnly?: boolean; // Ancalagon-staff-only: hidden from the nav unless user.is_ancalagon_staff
}

export interface IAppRouteGroup {
  label: string;
  routes: IAppRoute[];
  adminOnly?: boolean; // Ancalagon-staff-only: the whole group is hidden unless user.is_ancalagon_staff
}

export type AppRouteConfig = IAppRoute | IAppRouteGroup;

// used for navigation panel
const routes: AppRouteConfig[] = [
  {
    label: 'Dashboard',
    path: '/',
    title: 'Main Dashboard',
  },

  {
    label: 'Organization',
    routes: [
      {
        label: 'Switch organization',
        path: '/organization/customers',
        title: 'View and impersonate other organizations',
      },
      {
        label: 'Billing',
        path: '/organization/billing',
        title: 'General and Billing information',
      },
      {
        label: 'Prices',
        path: '/organization/prices',
        title: 'View your organization pricing',
      },
      {
        label: 'Invoices',
        path: '/organization/invoices',
        title: 'Invoice list',
      },
      {
        label: 'Agreements',
        path: '/organization/agreements',
        title: 'Special billing agreements for your organization',
      },
      {
        label: 'Team',
        path: '/organization/team',
        title: 'Manage your organization team',
      },
      {
        label: 'Domains',
        path: '/organization/domains',
        title: 'Register and verify organization domains',
      },
      {
        label: 'Agent',
        path: '/organization/agent',
        title: 'Enable and deploy the Manager Agent for your organization',
      },
    ],
  },
  {
    label: 'Data Center',
    routes: [
      {
        label: 'Overview',
        path: '/datacenter',
        title: 'Overview',
      },
      {
        label: 'Domain Names',
        path: '/datacenter/domains',
        title: 'Manage your domain names and DNS records',
      },
      {
        label: 'Emails',
        path: '/datacenter/emails',
        title: 'Manage mail domains, mailboxes, and aliases',
      },
      {
        label: 'Servers',
        path: '/datacenter/servers',
        title: 'Manage your servers',
      },
      {
        label: 'Virtual Machines',
        path: '/datacenter/virtual-machines',
        title: 'Manage your virtual machines',
      },
      {
        label: 'GPU Servers',
        path: '/datacenter/gpu-servers',
        title: 'Rent GPU and CPU servers by the hour',
      },
      {
        label: 'Containers',
        path: '/datacenter/containers',
        title: 'Manage your container workloads',
      },
      {
        label: 'Networks',
        path: '/datacenter/networks',
        title: 'Manage your VLAN and network list',
      },
      {
        label: 'VPN',
        path: '/datacenter/vpn',
        title: 'Manage your VPN access',
      },
      {
        label: 'Certificates',
        path: '/datacenter/certificates',
        title: 'Manage your TLS/SSL certificates',
      },
      {
        label: 'Backups',
        path: '/datacenter/backups',
        title: 'Manage your backups',
      },
      {
        label: 'License & Subscriptions',
        path: '/datacenter/licenses',
        title: 'Manage your software licenses and subscriptions',
      },
      {
        label: 'Public Keys',
        path: '/datacenter/pubkeys',
        title: 'Manage your public keys',
      },
    ],
  },
  {
    label: 'Support',
    path: '/support',
    title: 'Support Page',
  },
  {
    label: 'Roadmap',
    path: '/roadmap',
    title: 'Product roadmap and feature status',
  },
  {
    // Ancalagon-staff-only admin area. The `adminOnly` flag hides this whole group from the
    // sidebar unless `user.is_ancalagon_staff` (filtered in AppLayout); each page also guards
    // itself (renders <NotFound /> for non-staff) so a typed URL can't reach it either.
    label: 'Admin',
    adminOnly: true,
    routes: [
      {
        label: 'Newsletter',
        path: '/admin/newsletter',
        title: 'Newsletter',
        adminOnly: true,
      },
      {
        label: 'Quotes',
        path: '/admin/quotes',
        title: 'Quotes',
        adminOnly: true,
      },
      {
        label: 'Products and Prices',
        path: '/admin/products-and-prices',
        title: 'Products and Prices',
        adminOnly: true,
      },
      {
        label: 'Domain Pricing',
        path: '/admin/domain-pricing',
        title: 'Domain Pricing',
        adminOnly: true,
      },
      {
        label: 'Certificate Platform',
        path: '/admin/certificate-platforms',
        title: 'Certificate Platform',
        adminOnly: true,
      },
      {
        label: 'Backup Attribution',
        path: '/admin/backup-attributions',
        title: 'Backup Attribution',
        adminOnly: true,
      },
      {
        label: 'SPLA',
        path: '/admin/spla',
        title: 'Microsoft SPLA reporting',
        adminOnly: true,
      },
      {
        label: 'Wallets',
        path: '/admin/wallets',
        title: 'Prepaid wallets',
        adminOnly: true,
      },
      {
        label: 'Verda',
        path: '/admin/verda',
        title: 'Verda',
        adminOnly: true,
      },
      {
        label: 'Comptabilité',
        path: '/admin/reconciliation',
        title: 'Comptabilité',
        adminOnly: true,
      },
    ],
  },
  {
    label: 'Privacy',
    path: '/privacy',
    title: 'Privacy',
  },
  {
    label: 'Terms of Service',
    path: '/tos',
    title: 'Terms of Service',
  },
];
// Used for actual Routing
const router = createBrowserRouter([
  {
    element: <AppLayout />,
    errorElement: <NotFound />,
    children: [
      {
        path: '/',
        index: true,
        element: <Dashboard />,
      },

      {
        path: 'profile',
        element: <Profile />,
      },
      {
        path: 'organization',
        children: [
          {
            path: 'customers',
            element: <Customers />,
            errorElement: <NotFound />,
          },
          {
            path: 'billing',
            element: <Organization />,
            errorElement: <NotFound />,
          },
          {
            path: 'prices',
            element: <Prices />,
            errorElement: <NotFound />,
          },
          {
            path: 'invoices',
            element: <Invoices />,
            errorElement: <NotFound />,
          },
          {
            path: 'agreements',
            element: <Agreements />,
            errorElement: <NotFound />,
          },
          {
            path: 'team',
            element: <Team />,
            errorElement: <NotFound />,
          },
          {
            path: 'domains',
            element: <Domains />,
            errorElement: <NotFound />,
          },
          {
            path: 'domains/new',
            element: <DomainDetail />,
            errorElement: <NotFound />,
          },
          {
            path: 'domains/:id',
            element: <DomainDetail />,
            errorElement: <NotFound />,
          },
          {
            path: 'domains/:id/delete',
            element: <DomainDeleteResources />,
            errorElement: <NotFound />,
          },
          {
            path: 'agent',
            element: <Agent />,
            errorElement: <NotFound />,
          },
        ],
      },
      {
        // Transient landing page after Mollie checkout ({FRONTEND_URL}/payment/return?inv=…) — a
        // sibling of `organization`, deliberately NOT in the `routes` nav array (no sidebar entry).
        path: 'payment/return',
        element: <PaymentReturn />,
        errorElement: <NotFound />,
      },
      {
        // Mollie return URL after a domain-order checkout ({FRONTEND_URL}/domain-orders/{id}/return).
        // A nav-less transient landing page (no sidebar entry), like payment/return.
        path: 'domain-orders/:id/return',
        element: <DomainOrderReturn />,
        errorElement: <NotFound />,
      },
      {
        path: 'datacenter',
        element: <Datacenter />,
      },
      {
        path: 'datacenter/domains',
        element: <DatacenterDomains />,
      },
      {
        // Literal path registered before the `:id` param sibling so it isn't captured as a domain id.
        path: 'datacenter/domains/register',
        element: <DomainRegistration />,
      },
      {
        // Literal path registered before the `:id` param sibling so it isn't captured as a domain id.
        path: 'datacenter/domains/transfer',
        element: <DomainTransfer />,
      },
      {
        // Literal path registered before the `:id` param sibling so it isn't captured as a domain id.
        // Internal trade: change the registrant ("owner") of a .be domain already registered here.
        path: 'datacenter/domains/trade',
        element: <DomainTrade />,
      },
      {
        path: 'datacenter/domains/:id',
        element: <DomainNameDetail />,
      },
      {
        path: 'datacenter/servers',
        element: <Servers />,
      },
      {
        path: 'datacenter/servers/:serverId', // :serverId is the dynamic parameter
        element: <ServerDetail />,
      },
      {
        path: 'datacenter/virtual-machines',
        element: <VirtualMachines />,
      },
      {
        path: 'datacenter/virtual-machines/:vmId',
        element: <VirtualMachineDetail />,
      },
      {
        path: 'datacenter/gpu-servers',
        element: <GpuServers />,
      },
      {
        // Literal path registered before the `:serverId` param sibling, following the repo's habit.
        path: 'datacenter/gpu-servers/new',
        element: <StartGpuServer />,
      },
      {
        path: 'datacenter/gpu-servers/:serverId',
        element: <GpuServerDetail />,
      },
      {
        path: 'datacenter/containers',
        element: <Containers />,
      },
      {
        path: 'datacenter/emails',
        element: <Emails />,
      },
      {
        path: 'datacenter/emails/:domainId',
        element: <EmailsDetail />,
      },
      {
        path: 'datacenter/networks',
        element: <Networks />,
      },
      {
        path: 'datacenter/networks/:networkId',
        element: <NetworkDetail />,
      },
      {
        path: 'datacenter/vpn',
        element: <Vpn />,
      },
      {
        path: 'datacenter/certificates',
        element: <Certificates />,
      },
      {
        path: 'datacenter/certificates/:certificateId',
        element: <CertificateDetail />,
      },
      {
        path: 'datacenter/pubkeys',
        element: <PubkeyList />,
      },
      {
        path: 'datacenter/pubkeys/create',
        element: <PubkeyCreate />,
      },
      {
        path: 'datacenter/pubkeys/:pubkeyId',
        element: <PubkeyDetail />,
      },
      {
        path: 'datacenter/pubkeys/:pubkeyId/edit',
        element: <PubkeyEdit />,
      },
      {
        path: 'datacenter/backups',
        element: <Backups />,
      },
      {
        path: 'datacenter/backups/:backupId',
        element: <BackupDetail />,
      },
      {
        path: 'datacenter/licenses',
        element: <Licenses />,
      },
      {
        path: 'support',
        element: <Support />,
      },
      {
        path: 'roadmap',
        element: <Roadmap />,
      },
      {
        // Ancalagon-staff-only admin area (frontend mirror of the backend `/admin` prefix).
        // Hiding the nav group is not enough — each element guards itself, rendering
        // <NotFound /> for non-staff so a directly-typed URL leaks nothing.
        path: 'admin',
        children: [
          {
            path: 'newsletter',
            element: <Newsletter />,
            errorElement: <NotFound />,
          },
          {
            path: 'quotes',
            element: <Quotes />,
            errorElement: <NotFound />,
          },
          {
            path: 'products-and-prices',
            element: <ProductsAndPrices />,
            errorElement: <NotFound />,
          },
          {
            path: 'domain-pricing',
            element: <DomainPricing />,
            errorElement: <NotFound />,
          },
          {
            path: 'certificate-platforms',
            element: <CertificatePlatforms />,
            errorElement: <NotFound />,
          },
          {
            path: 'backup-attributions',
            element: <BackupAttributions />,
            errorElement: <NotFound />,
          },
          {
            path: 'spla',
            element: <Spla />,
            errorElement: <NotFound />,
          },
          {
            path: 'wallets',
            element: <Wallets />,
            errorElement: <NotFound />,
          },
          {
            path: 'wallets/:clientId',
            element: <WalletDetail />,
            errorElement: <NotFound />,
          },
          {
            path: 'verda',
            element: <Verda />,
            errorElement: <NotFound />,
          },
          {
            path: 'verda/servers/:serverId',
            element: <VerdaServerDetail />,
            errorElement: <NotFound />,
          },
          {
            path: 'reconciliation',
            element: <Reconciliation />,
            errorElement: <NotFound />,
          },
          {
            path: 'reconciliation/settlements/:settlementId',
            element: <ReconciliationSettlementDetail />,
            errorElement: <NotFound />,
          },
        ],
      },
      {
        path: 'privacy',
        element: <Privacy />,
      },
      {
        path: 'tos',
        element: <Tos />,
      },
    ],
  },
]);

// a custom hook for sending focus to the primary content container
// after a view has loaded so that subsequent press of tab key
// sends focus directly to relevant content
// may not be necessary if https://github.com/ReactTraining/react-router/issues/5210 is resolved
/*const useA11yRouteChange = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    routeFocusTimer = window.setTimeout(() => {
      const mainContainer = document.getElementById('primary-app-container');
      if (mainContainer) {
        mainContainer.focus();
      }
    }, 50);
    return () => {
      window.clearTimeout(routeFocusTimer);
    };
  }, [pathname]);
};

const RouteWithTitleUpdates = ({ component: Component, title, ...rest }: IAppRoute) => {
  useA11yRouteChange();
  useDocumentTitle(title);

  function routeWithTitle(routeProps: RouteComponentProps) {
    return <Component {...rest} {...routeProps} />;
  }

  return <Route render={routeWithTitle} {...rest} />;
};

const PageNotFound = ({ title }: { title: string }) => {
  useDocumentTitle(title);
  return <Route component={NotFound} />;
};

const flattenedRoutes: IAppRoute[] = routes.reduce(
  (flattened, route) => [...flattened, ...(route.routes ? route.routes : [route])],
  [] as IAppRoute[],
);

const AppRoutes = (): React.ReactElement => (
  <Switch>
    {flattenedRoutes.map(({ path, exact, component, title }, idx) => (
      <RouteWithTitleUpdates path={path} exact={exact} component={component} key={idx} title={title} />
    ))}
    <PageNotFound title="404 Page Not Found" />
  </Switch>
);*/
const AppRoutes = (): React.ReactElement => <RouterProvider router={router} />;
export { AppRoutes, routes, router };
